---
title: Agents that run agents
lede: The session's agent can plan its own workflow, start agents to work in parallel, run its projects, and land its work when you let it.
order: 33
part: Plan the work
---

A session's agent can do more than write code. It can split the rest of its task into a workflow, start other agents on pieces of it, run and restart the session's projects, and put a page or a call in front of you. With the landing settings on, a finished session can even be merged, pushed and opened as a pull request without you.

The tools are in every session, for both Claude Code and Codex. What they reach is the session's own machine and the workspace's settings, nothing more.

### Let the agent propose the workflow

On the task form, open the **Workflow** chip and pick **Agent proposes**. The task then starts on a single step, **Plan**: the agent reads the task and the code, proposes the steps, and stops.

![The run panel showing a workflow the agent proposed, waiting for Accept](media/agent-proposal.webp)

The proposal opens from the card's **Review** button on the Board, or in the run panel: press **Workflow** in the window bar (`⌥⌘W`). It lists each step with its model, and whether it runs in a new conversation, leaves notes on the code, waits for you, or runs a check.

| Button | What it does |
|---|---|
| **Accept** | Runs the proposed steps from the first one |
| **Edit** | Opens the chain on the **Workflows** canvas. What you change there is what **Accept** runs |
| **Decline** | Works the task in one go, without a workflow. On a workflow you chose, carries on with it as drawn |

To skip the review, set **Settings › Sessions › Accept the agent's plan** to **By itself**. You can still edit the chain while it runs.

When one of your workflows fits the task, the agent proposes that one rather than writing its own. A chain the agent wrote belongs to its task and goes with its run. To reuse it, press **Keep this workflow** under its steps, in the run panel, and name it.

### When the agent changes the plan mid-run

Who has to agree depends on whose workflow is running.

| The task runs | The agent can |
|---|---|
| No workflow, or one the agent wrote | Set or rewrite the rest of the run. Airlock follows it when the step it is on ends, without asking |
| A workflow you chose | Only propose. The run waits for you when the step it is on ends; **Decline** keeps your workflow as drawn |

The agent never writes a check command into a step. A step can only ask for the projects' own checks, on the changed projects or on all of them.

### Agents started by the agent

When a task splits into pieces that do not depend on each other, the agent can start other agents to work them at the same time. Each opens as a tab beside the session's agent, named after its agent and its job, such as **Claude Code · <name>**, works in the same files, and writes a report when it is done.

- Up to three run at once, and twelve over the session's life.
- The session's agent waits for every report before it hands back. A workflow step is not taken as done while an agent it started is still working.
- If Airlock restarts, the session's agent can no longer wait on the agents it started; check their tabs yourself.

See [Side agents](/side-agents) for the tabs themselves.

### What else the agent can do

| It can | Limits |
|---|---|
| End the step it is on with its report, which sends the run on even if Airlock did not notice the agent stopped. Or say what it needs, or that it could not do it | Only the current step of this session's run. A step's check and gate still apply, as do a proposal or a question waiting for you, and steps run by this computer end on their own. Each call, refused or not, is on the **Agent MCP** trail |
| List, start, stop and restart the session's projects, and read their logs (up to 400 lines) | Only the projects of this session, in its own machine |
| Save a call into the [API console](/api-console), with its OpenAPI document | It writes the call; it never sends it |
| Open a page in the **Browser**, or a call in the **API console**, in front of you | Only this session's own ports |
| Read the workspace's configuration, and correct a project's port, kind or variables | Written at once for every session of the workspace, and you are told. Never egress hosts, the session image, services on this computer or a secret's value: those it names in its report for you to change |
| Propose a project's install, start or check command, or its health check | Written only once you accept it on a card, since every later session runs it |
| List the session's public URLs, open one, and close one | Opening asks you on a card; it puts the port on the internet. Tunnels must be on, with ngrok set up |
| List the workspace's [targets](/targets) and switch the session to one | Done at once between **Local** and targets reached through this computer. To or from a direct-access target, it asks you on a card, as the machine restarts |

### Land a finished session by itself

**Settings › Git & merge › When a session is finished** lands a session once the agent has handed back, no step is left to run, and nothing was merged yet. Each switch needs the one above it.

| Setting | What it does |
|---|---|
| **Name the branch** | Lands on a new branch named `airlock/<title>-<id>`, or on the branch the session already names |
| **Write the commit and merge** | Asks the agent for the commit's title and description, then merges with them |
| **Push** | Pushes the branch to the repository's remote |
| **Open the pull request** | Against the branch the session was cut from. The repository must be on GitHub |
| **Then the session** | **Stays open**, **Stopped** (machine stopped, disk and conversation kept), or **Closed** (task filed in the Archive) |

All are off by default, and a workspace can set its own in **Workspace settings › Git & merge**. Each session is tried once per launch of Airlock. The first refusal stops the landing and says why at the head of the review, under **Landing by itself stopped**; what already landed stays, and you finish the rest by hand.

:::warn Landing means merging without review
With these on, the agent's work reaches your branch, and possibly your remote, without anyone reading it. Pair them with a workflow that reviews the work first.
:::

### Workflow defaults

**Settings › Sessions › Workflow** sets how new tasks start.

| Setting | Default | What it does |
|---|---|---|
| **New sessions are worked as** | **One instruction** | **One instruction**, **Agent proposes**, or **A workflow**. The task form can still pick another |
| **Default workflow** | — | The workflow a new task runs, when the setting above is **A workflow** |
| **Accept the agent's plan** | **After review** | **By itself** runs the agent's proposal at once |
